import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, KeyRound, Copy, CheckCircle2, Activity, RefreshCw, AlertCircle } from "lucide-react";
import api from "@/dashboard/api";

const fieldCls =
  "w-full border border-brand-border rounded-lg px-4 py-3 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 font-mono text-sm";
const labelCls = "block text-xs font-display font-semibold uppercase tracking-wider text-brand-slate mb-2";

const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`;

const Settings = () => {
  const [form, setForm] = useState({
    access_token: "", phone_number_id: "", business_account_id: "",
    webhook_verify_token: "", graph_api_version: "v21.0", app_secret: "",
  });
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const loadLogs = () => {
    setLogsLoading(true);
    api.get("/whatsapp/webhook-logs")
      .then((r) => setLogs(r.data.logs || []))
      .catch(() => {})
      .finally(() => setLogsLoading(false));
  };

  useEffect(() => {
    api.get("/whatsapp/settings").then((r) => {
      const d = r.data;
      setConfigured(d.configured);
      setForm((f) => ({
        ...f,
        phone_number_id: d.phone_number_id || "",
        business_account_id: d.business_account_id || "",
        graph_api_version: d.graph_api_version || "v21.0",
      }));
    }).catch(() => {}).finally(() => setLoading(false));
    loadLogs();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/whatsapp/settings", form);
      toast.success("Settings saved");
      setConfigured(true);
      // clear secret fields from state after save
      setForm((f) => ({ ...f, access_token: "", webhook_verify_token: f.webhook_verify_token, app_secret: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(WEBHOOK_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;

  return (
    <div data-testid="settings-page">
      <h1 className="font-display font-bold text-3xl text-brand-ink mb-1">Settings</h1>
      <p className="text-brand-muted mb-8">Meta WhatsApp Business Cloud API credentials.</p>

      {configured && (
        <div className="mb-6 flex items-center gap-2 bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm" data-testid="settings-configured-badge">
          <CheckCircle2 className="w-4 h-4" /> WhatsApp is configured and ready.
        </div>
      )}

      {/* Webhook config helper */}
      <div className="bg-white rounded-2xl border border-brand-border p-6 card-soft mb-6 max-w-2xl">
        <div className="font-display font-bold text-brand-ink mb-3">Webhook Configuration</div>
        <p className="text-sm text-brand-muted mb-4">Add this Callback URL in your Meta App → WhatsApp → Configuration, and set the Verify Token to match the one below.</p>
        <label className={labelCls}>Callback URL</label>
        <div className="flex gap-2">
          <input readOnly value={WEBHOOK_URL} className={`${fieldCls} bg-brand-surface`} data-testid="webhook-url" />
          <button onClick={copyWebhook} className="inline-flex items-center gap-2 px-4 rounded-lg border border-brand-border hover:bg-brand-surface text-sm font-medium" data-testid="copy-webhook">
            {copied ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <form onSubmit={save} data-testid="settings-form" className="bg-white rounded-2xl border border-brand-border p-6 md:p-8 card-soft max-w-2xl">
        <div className="flex items-center gap-2 mb-6">
          <KeyRound className="w-5 h-5 text-brand-blue" />
          <span className="font-display font-bold text-brand-ink">API Credentials</span>
        </div>
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Access Token {configured && <span className="text-brand-muted normal-case">(leave blank to keep current)</span>}</label>
            <input type="password" value={form.access_token} onChange={(e) => set("access_token", e.target.value)} placeholder="EAAG..." className={fieldCls} data-testid="settings-access-token" />
          </div>
          <div>
            <label className={labelCls}>Phone Number ID</label>
            <input value={form.phone_number_id} onChange={(e) => set("phone_number_id", e.target.value)} placeholder="123456789012345" className={fieldCls} data-testid="settings-phone-id" />
          </div>
          <div>
            <label className={labelCls}>Business Account ID</label>
            <input value={form.business_account_id} onChange={(e) => set("business_account_id", e.target.value)} placeholder="987654321098765" className={fieldCls} data-testid="settings-waba-id" />
          </div>
          <div>
            <label className={labelCls}>Webhook Verify Token {configured && <span className="text-brand-muted normal-case">(leave blank to keep current)</span>}</label>
            <input value={form.webhook_verify_token} onChange={(e) => set("webhook_verify_token", e.target.value)} placeholder="your-secret-verify-token" className={fieldCls} data-testid="settings-verify-token" />
          </div>
          <div>
            <label className={labelCls}>App Secret {configured && <span className="text-brand-muted normal-case">(leave blank to keep current)</span>}</label>
            <input type="password" value={form.app_secret} onChange={(e) => set("app_secret", e.target.value)} placeholder="Used to verify webhook signatures (optional)" className={fieldCls} data-testid="settings-app-secret" />
          </div>
          <div>
            <label className={labelCls}>Graph API Version</label>
            <input value={form.graph_api_version} onChange={(e) => set("graph_api_version", e.target.value)} placeholder="v21.0" className={fieldCls} data-testid="settings-graph-version" />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="settings-save" className="mt-7 inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white font-display font-semibold rounded-lg px-6 py-3 transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </form>

      {/* Webhook Diagnostics */}
      <div className="bg-white rounded-2xl border border-brand-border p-6 md:p-8 card-soft max-w-2xl mt-6" data-testid="webhook-diagnostics">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-brand-blue" />
            <span className="font-display font-bold text-brand-ink">Webhook Diagnostics</span>
          </div>
          <button onClick={loadLogs} className="inline-flex items-center gap-2 text-sm text-brand-slate hover:text-brand-blue" data-testid="refresh-logs">
            <RefreshCw className={`w-4 h-4 ${logsLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <p className="text-sm text-brand-muted mb-4">
          Every request Meta sends to your webhook is logged here. If this list stays empty after you message your number,
          Meta is not reaching your webhook — re-check the Callback URL, Verify Token, and that the <strong>messages</strong> field is subscribed.
        </p>
        {logs.length === 0 ? (
          <div className="flex items-center gap-2 bg-amber-50 text-amber-800 rounded-lg px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> No webhook events received yet.
          </div>
        ) : (
          <div className="max-h-64 overflow-auto rounded-lg border border-brand-border divide-y divide-brand-border">
            {logs.map((l) => (
              <div key={l.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-brand-ink">{l.object || "event"}</span>
                  <span className="text-xs text-brand-muted">{new Date(l.received_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-brand-muted mt-1">
                  signature: {l.has_signature ? "present" : "none"}
                </div>
                <pre className="text-[11px] text-brand-slate bg-brand-surface rounded p-2 mt-2 overflow-auto max-h-24 whitespace-pre-wrap break-all">{l.body_preview}</pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
